'use client';

import {
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Box, HStack } from '@terra/ui-styled-system';
import { BodyDefault } from '@terra/ui-design-system';
import { LinkSquare02Icon, Message01Icon } from '@terra/ui-icons';
import { useMainContentTabs } from '../../store/layoutStore';
import { useChatStore } from '../../store/chatStore';
import {
  GlobalMenuItemsTypeEnum,
  useGlobalContextMenuStore,
} from '../../store/globalContextMenuStore';
import { useChatSelectionContextMenu } from '../../hooks/useChatSelectionContextMenu';

const MENU_ITEM_ICONS = {
  [GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB]: LinkSquare02Icon,
  [GlobalMenuItemsTypeEnum.SEND_TO_CHAT]: Message01Icon,
} as const;
const MENU_OFFSET_PX = 4;
const VIEWPORT_PADDING_PX = 8;
const MENU_ITEMS_LABELS = {
  [GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB]: 'Open in new tab',
  [GlobalMenuItemsTypeEnum.SEND_TO_CHAT]: 'Send to Chat',
};

export function GlobalContextMenu() {
  useChatSelectionContextMenu();
  const { openInNewTab } = useMainContentTabs();
  const menuState = useGlobalContextMenuStore((state) => state.menuState);
  const closeMenu = useGlobalContextMenuStore((state) => state.closeMenu);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);
  const [menuDimensions, setMenuDimensions] = useState({
    width: 0,
    height: 0,
  });
  const handleMenuItemClick = useCallback(
    (itemType: string) => {
      const clickedItem = menuState.items.find(
        (item) => item.type === itemType,
      );

      if (!clickedItem) {
        return;
      }

      if (clickedItem.type === GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB) {
        openInNewTab(clickedItem.tab);
        clickedItem.onClick?.();
      }

      if (clickedItem.type === GlobalMenuItemsTypeEnum.SEND_TO_CHAT) {
        useChatStore.getState().sendHttpMentionsToChat({
          mentions: [clickedItem.mention],
          contextMessage: clickedItem.contextMessage,
          linkedObject: clickedItem.linkedObject,
        });
        clickedItem.onClick?.();
      }

      closeMenu();
    },
    [closeMenu, menuState.items, openInNewTab],
  );

  useEffect(() => {
    if (!menuState.isOpen) {
      return;
    }

    const handleGlobalPointerDown = (event: PointerEvent) => {
      const eventTarget = event.target;

      if (
        menuContainerRef.current &&
        eventTarget instanceof Node &&
        menuContainerRef.current.contains(eventTarget)
      ) {
        return;
      }

      closeMenu();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    const handleScroll = (_event: WheelEvent) => {
      closeMenu();
    };

    document.addEventListener('pointerdown', handleGlobalPointerDown);
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('wheel', handleScroll);

    return () => {
      document.removeEventListener('pointerdown', handleGlobalPointerDown);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('wheel', handleScroll);
    };
  }, [closeMenu, menuState.isOpen]);
  useLayoutEffect(() => {
    if (!menuState.isOpen || !menuContainerRef.current) {
      return;
    }

    const rect = menuContainerRef.current.getBoundingClientRect();

    setMenuDimensions((previousDimensions) => {
      if (
        previousDimensions.width === rect.width &&
        previousDimensions.height === rect.height
      ) {
        return previousDimensions;
      }

      return {
        width: rect.width,
        height: rect.height,
      };
    });
  }, [menuState.isOpen, menuState.x, menuState.y]);
  if (!menuState.isOpen || typeof document === 'undefined') {
    return null;
  }

  const menuLeft =
    typeof window === 'undefined'
      ? menuState.x + MENU_OFFSET_PX
      : Math.max(
          VIEWPORT_PADDING_PX,
          Math.min(
            menuState.x + MENU_OFFSET_PX,
            window.innerWidth - menuDimensions.width - VIEWPORT_PADDING_PX,
          ),
        );
  const menuTop =
    typeof window === 'undefined'
      ? menuState.y + MENU_OFFSET_PX
      : Math.max(
          VIEWPORT_PADDING_PX,
          Math.min(
            menuState.y + MENU_OFFSET_PX,
            window.innerHeight - menuDimensions.height - VIEWPORT_PADDING_PX,
          ),
        );

  return createPortal(
    <Box
      ref={menuContainerRef}
      position="fixed"
      style={{
        top: `${menuTop}px`,
        left: `${menuLeft}px`,
      }}
      bg="bg.default"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="sm"
      py="1"
      minW="160px"
      zIndex={3000}
      boxShadow="md"
      onContextMenu={(event) => event.preventDefault()}
    >
      {menuState.items.map((item, index) => {
        const ItemIcon = MENU_ITEM_ICONS[item.type];

        return (
          <Box
            key={`${item.type}-${index}`}
            px="2"
            py="1"
            cursor="pointer"
            _hover={{ bg: 'bg.subtle' }}
            onClick={() => handleMenuItemClick(item.type)}
          >
            <HStack gap="1.5">
              <ItemIcon size={12} />
              <BodyDefault color="fg.default">
                {MENU_ITEMS_LABELS[item.type]}
              </BodyDefault>
            </HStack>
          </Box>
        );
      })}
    </Box>,
    document.body,
  );
}
